<script setup lang="ts">
import { OverlayScrollbarsComponent } from "overlayscrollbars-vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    alwaysVisible?: boolean;
    extraOverscroll?: boolean;
    contentClasses?: string;
  }>(),
  {
    alwaysVisible: false,
    contentClasses: "",
  }
);

const emit = defineEmits<{
  scroll: [ev: Event];
  viewportChange: [];
}>();

const osInstance = ref<InstanceType<typeof OverlayScrollbarsComponent> | null>(null);

const getScrollElement = () => osInstance.value?.osInstance()?.elements().viewport as HTMLElement | null;

const applyInnerClasses = async (newClasses: string, oldClasses?: string) => {
  await nextTick();
  if (!osInstance.value) {
    return;
  }

  const contentElement = osInstance.value.osInstance()?.elements().content;
  if (!contentElement) {
    return;
  }

  if (oldClasses) {
    contentElement.classList.remove(...oldClasses.split(" ").filter(Boolean));
  }

  if (newClasses) {
    contentElement.classList.add(...newClasses.split(" ").filter(Boolean));
  }
};

onMounted(() => {
  applyInnerClasses(props.contentClasses);
});

watch(() => props.contentClasses, applyInnerClasses);

let detachScroll: (() => void) | null = null;
let contentRO: ResizeObserver | null = null;
let detachOsUpdated: (() => void) | null = null;

const triggerViewportChange = () => {
  emit("scroll", new Event("update"));
  emit("viewportChange");
};

const attachScrollListener = async () => {
  await nextTick();
  const el = getScrollElement();
  if (!el) {
    return;
  }

  const handler = (ev: Event) => {
    emit("scroll", ev);
    emit("viewportChange");
  };
  el.addEventListener("scroll", handler, { passive: true });
  detachScroll = () => el.removeEventListener("scroll", handler as EventListener);
};

const attachContentResizeObserver = async () => {
  await nextTick();
  if (!osInstance.value) {
    return;
  }
  const content = osInstance.value.osInstance()?.elements().content as HTMLElement | null;
  if (!content) {
    return;
  }
  contentRO = new ResizeObserver(() => {
    // Trigger the same handler pathway as scroll to update dependent UIs
    triggerViewportChange();
    requestAnimationFrame(triggerViewportChange);
  });
  contentRO.observe(content);
};

const attachOsUpdatedListener = async () => {
  await nextTick();
  const os = osInstance.value?.osInstance() as unknown as {
    on?: (name: "updated", handler: () => void) => (() => void) | void;
    off?: (name: "updated", handler: () => void) => void;
  };
  if (!os) {
    return;
  }
  const handler = () => {
    triggerViewportChange();
  };
  try {
    let ret: (() => void) | void | undefined;
    if (os.on) {
      ret = os.on("updated", handler);
    }
    if (typeof ret === "function") {
      detachOsUpdated = ret;
    } else if (os.off) {
      const { off } = os;
      detachOsUpdated = () => off("updated", handler);
    }
  } catch (error) {
    // ignore
  }
};

watch(
  () => osInstance.value?.osInstance(),
  async () => {
    if (detachScroll) {
      detachScroll();
      detachScroll = null;
    }
    if (contentRO) {
      contentRO.disconnect();
      contentRO = null;
    }
    await attachScrollListener();
    await attachContentResizeObserver();
    await attachOsUpdatedListener();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (detachScroll) {
    detachScroll();
    detachScroll = null;
  }
  if (contentRO) {
    contentRO.disconnect();
    contentRO = null;
  }
  if (detachOsUpdated) {
    try {
      detachOsUpdated();
    } catch (error) {
      // ignore
    }
    detachOsUpdated = null;
  }
});

defineExpose({
  scrollElement: computed(() => getScrollElement()),
});
</script>

<template>
  <OverlayScrollbarsComponent
    ref="osInstance"
    element="div"
    :options="{
      overflow: {
        x: 'hidden',
      },
      scrollbars: {
        autoHide: props.alwaysVisible ? 'never' : 'leave',
        autoHideDelay: 800,
      },
    }"
    class="[&_.os-scrollbar]:!inset-y-1"
    :class="props.extraOverscroll ? 'pb-[300px]' : ''">
    <slot />
  </OverlayScrollbarsComponent>
</template>
